import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { apiClient } from '../lib/api';
import { Category } from '../types';
import { PlusIcon, PencilIcon, TrashIcon, StarIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import CategoryModal from './CategoryModal';
import { AxiosError } from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';

const CategoriesList: React.FC = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | undefined>(undefined);

  const { admin } = useAuth();
  if (!hasPermission(admin, 'categories', 'read')) {
    return <div className="text-red-600">У вас нет прав для просмотра категорий.</div>;
  }

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading, error, refetch } = useQuery(
    ['categories', page, debouncedSearch],
    () => apiClient.getCategories({
      page,
      limit: 10,
      search: debouncedSearch,
    })
  );

  const handleDelete = async (id: string) => {
    if (window.confirm('Вы уверены, что хотите удалить эту категорию?')) {
      try {
        await apiClient.deleteCategory(id);
        toast.success('Категория удалена успешно');
        queryClient.invalidateQueries(['categories']);
      } catch (error: any) {
        toast.error(error.response?.data?.error || 'Ошибка удаления категории');
      }
    }
  };

  const handleSetDefault = async (id: string) => {
    try {
      const category = categories.find(c => c.id === id);
      await apiClient.setDefaultCategory(id);
      
      if (category?.is_default) {
        toast.success('Категория снята с категории по умолчанию');
      } else {
        toast.success('Категория установлена по умолчанию');
      }
      
      // Обновляем данные локально
      queryClient.setQueryData(['categories', page, search], (oldData: any) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          data: oldData.data.map((cat: Category) => 
            cat.id === id ? { ...cat, is_default: !cat.is_default } : cat
          )
        };
      });
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Ошибка изменения статуса категории по умолчанию');
    }
  };

  const handleCreateCategory = () => {
    setEditingCategory(undefined);
    setIsModalOpen(true);
  };

  const handleEditCategory = (category: Category) => {
    setEditingCategory(category);
    setIsModalOpen(true);
  };

  const handleSaveCategory = async (categoryData: Partial<Category>) => {
    try {
      if (editingCategory) {
        await apiClient.updateCategory(editingCategory.id, categoryData as any);
        queryClient.invalidateQueries(['categories']);
      } else {
        await apiClient.createCategory(categoryData as any);
        queryClient.invalidateQueries(['categories']);
      }
    } catch (error: any) {
      throw error;
    }
  };

  if (isLoading) return <div>Загрузка...</div>;
  if (error) {
    const status = (error as AxiosError)?.response?.status;
    if (status === 403) {
      return <div className="text-red-600">У вас нет прав для просмотра категорий. Обратитесь к администратору.</div>;
    }
    return <div>Ошибка загрузки категорий</div>;
  }

  const categories = data?.data || [];
  const pagination = data?.pagination;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Категории</h1>
        <button 
          onClick={handleCreateCategory}
          className="btn btn-primary flex items-center"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Создать категорию
        </button>
      </div>

      {/* Search */}
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex-1 min-w-0">
          <input
            type="text"
            placeholder="Поиск категорий..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div className="flex flex-col sm:flex-row gap-4 lg:flex-row">
          <button
            onClick={() => setSearch('')}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 min-w-[100px]"
          >
            Сбросить
          </button>
        </div>
      </div>

      {/* Categories Table */}
      <div className="bg-white shadow rounded-lg">
        {/* Mobile view - cards */}
        <div className="md:hidden space-y-4 p-4">
          {categories.map((category: Category) => (
            <div key={category.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex justify-between items-start mb-3">
                <h3 className="text-sm font-medium text-gray-900 truncate flex-1 mr-2">
                  {category.name}
                </h3>
                <div className="flex space-x-2 flex-shrink-0">
                  <button
                    onClick={() => handleSetDefault(category.id)}
                    className={`${
                      category.is_default 
                        ? 'text-yellow-500 hover:text-yellow-600' 
                        : 'text-gray-400 hover:text-yellow-500'
                    } p-1`}
                    title={category.is_default ? 'Снять с категории по умолчанию' : 'Установить как категорию по умолчанию'}
                  >
                    <StarIcon className="h-5 w-5" />
                  </button>
                  <button 
                    onClick={() => handleEditCategory(category)}
                    className="text-primary-600 hover:text-primary-900 p-1"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(category.id)}
                    className="text-red-600 hover:text-red-900 p-1"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="space-y-2 text-xs text-gray-500">
                <div className="text-xs">
                  <div className="font-medium text-gray-700">Slug:</div>
                  <div className="font-mono">{category.slug}</div>
                </div>
                {category.description && (
                  <div className="text-xs">
                    <div className="font-medium text-gray-700">Описание:</div>
                    <div className="truncate">{category.description}</div>
                  </div>
                )}
                <div className="flex items-center space-x-2">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    category.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {category.is_active ? 'Активна' : 'Неактивна'}
                  </span>
                  {category.is_default && (
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                      По умолчанию
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Desktop view - table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Название
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Slug
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Описание
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Статус
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  По умолчанию
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Действия
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {categories.map((category: Category) => (
                <tr key={category.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {category.name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {category.slug}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    <div className="max-w-xs truncate">
                      {category.description}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      category.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {category.is_active ? 'Активна' : 'Неактивна'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleSetDefault(category.id)}
                      className={`${
                        category.is_default 
                          ? 'text-yellow-500 hover:text-yellow-600' 
                          : 'text-gray-400 hover:text-yellow-500'
                      }`}
                      title={category.is_default ? 'Снять с категории по умолчанию' : 'Установить как категорию по умолчанию'}
                    >
                      <StarIcon className="h-5 w-5" />
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button 
                        onClick={() => handleEditCategory(category)}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(category.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Предыдущая
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= pagination.pages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Следующая
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Показано <span className="font-medium">{((page - 1) * 10) + 1}</span> до{' '}
                  <span className="font-medium">
                    {Math.min(page * 10, pagination.total)}
                  </span>{' '}
                  из <span className="font-medium">{pagination.total}</span> результатов
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Предыдущая
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page >= pagination.pages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Следующая
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      <CategoryModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        category={editingCategory}
        onSave={handleSaveCategory}
      />
    </div>
  );
};

export default CategoriesList; 